import { useTranslation } from "react-i18next";
import { RouterProvider } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/react";
import { App as AntdApp, ConfigProvider, Spin } from "antd";
import arEG from "antd/locale/ar_EG";
import bgBG from "antd/locale/bg_BG";
import beBY from "antd/locale/by_BY";
import csCZ from "antd/locale/cs_CZ";
import deDE from "antd/locale/de_DE";
import enUS from "antd/locale/en_US";
import esES from "antd/locale/es_ES";
import frFR from "antd/locale/fr_FR";
import heIL from "antd/locale/he_IL";
import itIT from "antd/locale/it_IT";
import jaJP from "antd/locale/ja_JP";
import plPL from "antd/locale/pl_PL";
import ptPT from "antd/locale/pt_PT";
import ruRU from "antd/locale/ru_RU";
import zhCN from "antd/locale/zh_CN";

import { router } from "./router";

const RTL_LANGS = new Set(["he", "ar"]);

const antdLocaleMap = {
  en: enUS,
  ru: ruRU,
  fr: frFR,
  de: deDE,
  es: esES,
  be: beBY,
  bg: bgBG,
  cs: csCZ,
  it: itIT,
  ja: jaJP,
  pl: plPL,
  pt: ptPT,
  zh: zhCN,
  he: heIL,
  ar: arEG,
};

function getAntdLocale(lang: string) {
  const language = lang.split("-")[0];
  return antdLocaleMap[language as keyof typeof antdLocaleMap] ?? enUS;
}

Spin.setDefaultIndicator(<LoadingOutlined style={{ fontSize: 32 }} spin />);

const theme = {
  token: {
    fontFamily: "'Roboto', 'Noto Sans Arabic', 'Noto Sans Hebrew', sans-serif",
    colorPrimary: "#0070c5",
    colorLink: "#0070c5",
    colorLinkHover: "#005a9e",
    colorLinkActive: "#004c8a",
  },
  components: {
    Form: {
      itemMarginBottom: 12,
    },
    Button: {
      fontFamily:
        "'Roboto', 'Noto Sans Arabic', 'Noto Sans Hebrew', sans-serif",
      fontWeight: 500,
      defaultHoverColor: "#0070c5",
      defaultHoverBorderColor: "#0070c5",
    },
  },
};

export default function App() {
  const { i18n } = useTranslation();
  const language = i18n.language.split("-")[0];
  const direction = RTL_LANGS.has(language) ? "rtl" : "ltr";
  return (
    <Sentry.ErrorBoundary>
      <ConfigProvider
        theme={theme}
        locale={getAntdLocale(i18n.language)}
        direction={direction}
      >
        <AntdApp>
          <RouterProvider router={router} />
        </AntdApp>
      </ConfigProvider>
    </Sentry.ErrorBoundary>
  );
}
